import { api } from "@/modules/api";
import { windowModule } from "@/modules/window";
import { components } from "@/__openapi__/backend/schema";
import type { Location } from "react-router-dom";
import { logger } from "../logger";
import { BaseError } from "@/domains/errors";
import { objectModule } from "../object";
import { buildStripeReturnUrl } from "./query-params";

interface GetBillingPortalParams {
  returnUrl: Location;
}

type BillingPortalResponse = components["schemas"]["GetStripeBillingPortalUrlResponseSchema"];

/**
 * Fetches the Stripe billing portal URL and navigates to it.
 * Used for existing subscribers to manage their subscription.
 * @param returnUrl - The location to return to after managing billing
 */
export async function getBillingPortalAndNavigate({
  returnUrl,
}: GetBillingPortalParams): Promise<BillingPortalResponse> {
  const returnUrlString = buildStripeReturnUrl({ location: returnUrl });

  const response = await api.get("/v2/me/subscriptions/stripe/billing-portal", {
    params: { query: { return_url: returnUrlString } },
  });

  if (!response.data) {
    logger.error({
      message: "Failed to get billing portal URL",
      info: { response: objectModule.safeAsJson(response) },
    });
    throw new BaseError({
      message: "Failed to get billing portal URL",
      info: { response: objectModule.safeAsJson(response) },
    });
  }

  if (response.data.stripe_billing_portal_url) {
    windowModule.navigateToExternal({ url: new URL(response.data.stripe_billing_portal_url) });
  }

  return response.data;
}
